import {
  computed,
  type ComputedRef,
} from 'vue'
import {
  type ImportJob,
  ImportJobStatus,
} from '~domain/io/ImportJob'


export function useImportJobStatus (job: ComputedRef<ImportJob | undefined>) {
  const amountSuccess = computed(() => {
    return (job.value?.amountDone ?? 0) - (job.value?.amountFailed ?? 0)
  })

  const isError = computed(() => {
    return job.value?.status === ImportJobStatus.FAILED
  })

  const isKilled = computed(() => {
    return job.value?.status === ImportJobStatus.KILLED
  })

  const isFinished = computed(() => {
    return job.value?.status === ImportJobStatus.DONE ||
      job.value?.status === ImportJobStatus.STOPPED
  })

  const isImportingFromEdge = computed(() => {
    // TODO: the 'edge_recording_id' attribute key should be a constant,
    // it is defined by the edge backend
    return !!job.value?.tagAttributes?.edge_recording_id
  })

  const isIncompatible = computed(() => {
    return job.value?.status === ImportJobStatus.INCOMPATIBLE_FORMAT
  })

  const isPartialSuccess = computed(() => {
    return job.value?.status === ImportJobStatus.DONE &&
      !!job.value?.amountFailed
  })

  const isProgress = computed(() => {
    return job.value?.status === ImportJobStatus.STARTING ||
      job.value?.status === ImportJobStatus.RUNNING
  })

  const isStopped = computed(() => {
    return job.value?.status === ImportJobStatus.STOPPED
  })

  const isStopping = computed(() => {
    return job.value?.status === ImportJobStatus.STOPPING
  })

  const isUploading = computed(() => {
    return job.value?.status === ImportJobStatus.UPLOADING
  })

  const isWaiting = computed(() => {
    return isUploading.value && progress.value === 1
  })

  const partialUpload = computed<[number, number]>(() => {
    const samplesFailed = job.value?.amountFailed ?? 0
    const samplesDone = job.value?.amountDone ?? 0
    const samplesTotal = job.value?.amountTotal ?? 0

    return isStopped.value
      ? [Math.max(0, samplesTotal - samplesDone - samplesFailed), samplesTotal]
      : [Math.max(0, samplesFailed), samplesDone]
  })

  const progress = computed(() => {
    if (!job.value) return 0

    switch (job.value.status) {
      case ImportJobStatus.STARTING:
      case ImportJobStatus.RUNNING:
      case ImportJobStatus.STOPPING:
        return job.value.progress
      case ImportJobStatus.UPLOADING:
        return job.value.uploadProgress ?? 0
      default:
        return 0
    }
  })

  return {
    amountSuccess,
    isError,
    isFinished,
    isImportingFromEdge,
    isIncompatible,
    isKilled,
    isPartialSuccess,
    isProgress,
    isStopped,
    isStopping,
    isUploading,
    isWaiting,
    partialUpload,
    progress,
  }
}
